/**
 * Ring System Configuration
 * 
 * Centralized configuration for all ring animations.
 * Easy to upgrade, modify, or extend without scattering logic.
 */

export type RingType = "brand" | "user";

export interface RingConfig {
  type: RingType;
  colors: string[]; // For brand: full spectrum. For user: single color array
  duration: number; // Animation duration in seconds
  opacity: number; // Ring opacity (0-1)
  blur: number; // Blur amount in pixels
  direction: "clockwise" | "counterclockwise";
  width: number; // Ring width in pixels
  reactive?: boolean; // Whether ring reacts to user input/state
}

/**
 * Brand ring configuration (landing page, marketing)
 * Full spectrum, slow, continuous, non-reactive
 */
export const BRAND_RING_CONFIG: RingConfig = {
  type: "brand",
  colors: [
    "#6366f1", // indigo
    "#8b5cf6", // violet
    "#a855f7", // purple
    "#c084fc", // fuchsia
    "#d946ef", // pink
    "#ec4899", // rose
    "#f43f5e", // red
    "#fb7185", // pink-400
    "#f97316", // orange
    "#fb923c", // orange-400
    "#fbbf24", // amber
    "#facc15", // yellow
    "#a3e635", // lime
    "#84cc16", // green
    "#22c55e", // emerald
    "#10b981", // green-500
    "#14b8a6", // teal
    "#06b6d4", // cyan
    "#3b82f6", // blue
    "#6366f1", // back to indigo (loop)
  ],
  duration: 30, // 25-40s range, using 30s
  opacity: 0.4, // Temporarily higher for visibility testing
  blur: 50, // Reduced for better visibility
  direction: "clockwise",
  width: 4, // Increased from 3 for better visibility
  reactive: false,
};

/**
 * User ring configuration (chat input, user-owned surfaces)
 * Single color, state-aware, reactive
 */
export function getUserRingConfig(userColor: string): RingConfig {
  return {
    type: "user",
    colors: [userColor], // Single color
    duration: 0, // No animation for idle state
    opacity: 0.35, // Temporarily higher for visibility testing
    blur: 25, // Reduced for better visibility
    direction: "clockwise",
    width: 3, // Increased from 2 for better visibility
    reactive: true,
  };
}

/**
 * User ring states (for reactive rings)
 */
export type UserRingState = "idle" | "typing" | "processing";

export interface UserRingStateConfig {
  opacity: number;
  pulse?: boolean;
  pulseSpeed?: number; // pulses per second
}

export const USER_RING_STATES: Record<UserRingState, UserRingStateConfig> = {
  idle: {
    opacity: 0.35,
    pulse: false,
  },
  typing: {
    opacity: 0.50, // More noticeable intensity increase
    pulse: false,
  },
  processing: {
    opacity: 0.45,
    pulse: true,
    pulseSpeed: 1.5, // Gentle pulse
  },
};


